import React, { createContext, useContext, useState, useEffect } from 'react';
import { AUTH_ENDPOINTS, STORAGE_KEYS, AUTH_VALIDITY_MS } from '../Utils/constants';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authGuid, setAuthGuid] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const storedAuth = localStorage.getItem(STORAGE_KEYS.uploadAuth);
    if (storedAuth) {
      try {
        const { guid, timestamp } = JSON.parse(storedAuth);
        // Check if auth is still valid (24 hours)
        if (Date.now() - timestamp < AUTH_VALIDITY_MS) {
          setAuthGuid(guid);
          setIsAuthenticated(true);
          return;
        }
      } catch (error) {
        console.error('Error parsing stored auth:', error);
      }
    }
    setIsAuthenticated(false);
  };

  const login = async (password) => {
    setAuthError('');
    setAuthLoading(true);
    
    try {
      const response = await fetch(AUTH_ENDPOINTS.uploadAuth, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
      });
      
      if (!response.ok) {
        throw new Error("Invalid password");
      }
      
      // Handle both JSON and plain text responses
      const contentType = response.headers.get("content-type");
      let guid;
      if (contentType && contentType.includes("application/json")) {
        const data = await response.json();
        guid = data.guid;
      } else {
        guid = await response.text();
      }
      
      if (!guid) {
        throw new Error("No GUID received");
      }
      
      setAuthGuid(guid);
      setIsAuthenticated(true);
      
      localStorage.setItem(STORAGE_KEYS.uploadAuth, JSON.stringify({
        guid,
        timestamp: Date.now()
      }));
      
      window.history.replaceState(null, '', `${window.location.pathname}?admin=true`);
      
      return { success: true };
    } catch (err) {
      setAuthError(err.message);
      return { success: false, error: err.message };
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.uploadAuth);
    setAuthGuid('');
    setIsAuthenticated(false);
    window.history.replaceState(null, '', window.location.pathname);
  };

  const getAuthHeaders = () => {
    if (!authGuid) return {};
    return {
      'X-Auth-Guid': authGuid,
      'Authorization': `Bearer ${authGuid}`
    };
  };

  const value = {
    isAuthenticated,
    authGuid,
    authLoading,
    authError,
    login,
    logout,
    getAuthHeaders
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};